import React, {useMemo, useEffect, useState, useReducer} from "react";
import {connect} from 'react-redux'
import {withRouter} from "react-router";
import {documentsMode, shareSystemsActiveArchive} from "libs/shareSystems";
import {DemandValidation} from 'constants/DemandValidation';
import Loading from "components/Loading";
import ArchivingService from "services/ArchivingService";
import Tr from "translations/Translations";
import useSnackbar from "hooks/useSnackbar";
import Typography from "components/Typography";
import GridBlock from "components/GridBlock";
import EzdrpService from "services/EzdrpService";
import EzdpuwService from "services/EzdpuwService";
import Button from "components/Button";
import ArchiveIcon from '@material-ui/icons/Archive';
import {makeStyles} from "@material-ui/core";
import {ArchivingStatus, statusScheduleAvailable} from "./constants/ArchivingStatus";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import DateFormats from "constants/DateFormats";
import moment from "libs/moment";
import downloadFile from "libs/downloadFile";
import {getUserFullNameWithDeputy} from "libs/userNameGetter";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";
import iconWorksReducer from "libs/iconWorksReducer";
import PrivilegeService from "services/PrivilegeService";

const useStyle = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    icon: theme.components.icon,
    text: {
        marginBottom: theme.spacing(2)
    },
    button: {
        width: 70,
        padding: '0 !important',
    },
}))

function ArchivingPanel(
    {
        match,
        demandValidation,
        demandArchiving,
        demandHeader,
        demandPrivileges
    }) {

    const classes = useStyle();
    const curDemandValidation = DemandValidation.ARCHIVING;
    const demandId = match.params.id;
    const section = demandValidation.find(x => x.section === curDemandValidation);
    const editable = section ? section.editable : false;
    const [working, setWorking] = useState(false)
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();
    const showShare = useMemo(() => demandPrivileges.INTEGRATIONS_UPLOAD && shareSystemsActiveArchive(demandHeader).length > 0, [demandHeader, demandPrivileges])

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        preview: false,
        download: false,
        publish: false,
        share: false,
        downloadCount: false
    });

    useEffect(() => {
        ArchivingService.getDemandArchiving(demandId);
        return () => {
            ArchivingService.clearDemandArchiving();
        }
    }, [demandId])

    const scheduleArchive = () => {
        ArchivingService.scheduleArchive(demandId);
    };

    const downloadArchive = () => {
        const archiveUrl = demandArchiving?.info?.archiveUrl;
        if (archiveUrl) {
            setWorking(true);
            downloadFile({url: archiveUrl.url, name: archiveUrl.name});
        }
    };

    const getDescription = () => {
        if (!demandArchiving.archivingStatus) {
            if (editable) {
                return Tr('demand_archiving_schedule_available')
            } else {
                return Tr('demand_archiving_schedule_unavailable')
            }
        } else {
            return Tr('demand_archiving_' + demandArchiving.archivingStatus.toLowerCase())
        }
    }

    const onShareFile = () => setShareFilePopup(sharePopupData(demandArchiving.info.demandNumber, null));
    const doShare = (payload, system) => {
        dispatchIconWorks({type: 'publish', works: true});
            if(system === 'ezdrp'){
                EzdrpService.shareArchiveEzdrp(demandId).then((resp) => {
                        if (resp)
                            enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
                        }).finally(() => {
                            dispatchIconWorks({type: 'publish', works: false})
                        });
            }else if(system === 'ezdpuw'){
                EzdpuwService.shareArchiveEzdpuw(demandId).then((resp) => {
                        if (resp)
                            enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
                        }).finally(() => {
                            dispatchIconWorks({type: 'publish', works: false})
                        });
            }
            setShareFilePopup(null);
    }

    const loadLastShareHistory = () => {
        return ArchivingService.loadLastShareHistory(demandId);
    }

    const loadShareHistory = (system) => {
        return ArchivingService.loadShareHistory(demandId, system);
    }

     const getArchiveMessage = () => {
        if ((demandArchiving.archivingStatus === ArchivingStatus.ARCHIVED || demandArchiving.archivingStatus === ArchivingStatus.ARCHIVE_EXPIRED)
         && (demandHeader?.applicationClientSettings?.ezdpuwIntegration?.documentsMode === documentsMode.ARCHIVE
                || demandHeader?.applicationClientSettings?.ezdrpIntegration?.documentsMode === documentsMode.ARCHIVE)) {
            return `${Tr('archive_transferred_to_ezd', {demand: `${demandArchiving?.info?.demandNumber} ${moment(demandArchiving?.info?.createDate).format(DateFormats.DATETIME_FORMAT)}`})}`;
        }
     }

    const getButton = () => {
        if (!demandArchiving.archivingStatus || demandArchiving.archivingStatus === ArchivingStatus.ARCHIVE_EXPIRED) {
            return <Button
                onClick={scheduleArchive}
                disabled={!editable || !statusScheduleAvailable(demandArchiving.archivingStatus)}
            >
                {Tr('demand_archiving_button')}
            </Button>
        } else if (demandArchiving.archivingStatus === ArchivingStatus.ARCHIVED) {
            return <Button
                onClick={downloadArchive}
                disabled={!editable || working}
            >
                {Tr('demand_archiving_button_download')}
            </Button>
        }
    }

    const shareArchive = () => {
        if(demandArchiving.archivingStatus === ArchivingStatus.ARCHIVED)
        return <>{showShare && <WorkableIconShareButton
                                    onClick={() => onShareFile()}
                                    disabled={iconWorks.anyWorks}
                                    working={iconWorks.publish}
                                />}
                                {!!shareFilePopup && <ShareSelectPopup
                                    data={shareFilePopup}
                                    onCancel={() => setShareFilePopup(null)}
                                    onChoose={doShare}
                                    isArchive={true}
                                    loadLastShareHistory={loadLastShareHistory}
                                    loadShareHistory={(system) => loadShareHistory(system)}
                                />
        }</>
    }

    const getHeaderClasses = () => {
        if (!demandArchiving) return;
        const headers = [];
        headers.push(undefined);
        headers.push(undefined);
        headers.push(undefined);
        headers.push(undefined);
        headers.push(undefined);
        if (showShare) {
            headers.push(classes.button);
        }

        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headerClasses = useMemo(getHeaderClasses, [demandArchiving]);

    if (!demandArchiving) return <Loading/>
    return (
        <GridBlock
            className={classes.root}
            direction='column'
            alignItems='center'

        >
            <ArchiveIcon className={classes.icon} color='primary'/>
            <Typography className={classes.text}>{getDescription()}</Typography>
            <Typography className={classes.text}>{getArchiveMessage()}</Typography>

            {!!demandArchiving.info &&
            <SimpleTable
                className={classes.text}
                columnClasses={headerClasses}
                 headers={[
                    Tr('case_number'),
                    Tr('proceedings_subject'),
                    Tr('demand_archiving_order_date'),
                    Tr('demand_archiving_create_date'),
                    Tr('demand_archiving_user'),
                    ...(showShare ? [Tr('send')] : [])
                ]
                }
                items={[[
                    demandArchiving.info.demandNumber,
                    demandArchiving.info.demandName,
                    moment(demandArchiving.info.orderDate).format(DateFormats.DATETIME_FORMAT),
                    demandArchiving.info.createDate ? moment(demandArchiving.info.createDate).format(DateFormats.DATETIME_FORMAT) : null,
                    getUserFullNameWithDeputy(demandArchiving.info.user),
                    ...(showShare ? [shareArchive(demandArchiving.info.user)] : [])
                ]]}
            />
            }
            {getButton()}
        </GridBlock>
    )
}


export default connect((state) => ({
    demandValidation: state.resources.demandValidation,
    demandArchiving: state.resources[ArchivingService.DEMAND_ARCHIVING_RESOURCE_ID],
    demandHeader: state.resources.demandHeader,
    demandPrivileges: state.privileges[PrivilegeService.DEMAND_PRIVILEGE_STORE_KEY]
}), {})(withRouter(ArchivingPanel));
